import {
  CompanyType,
  DOCUMENT_GROUP_KEYS,
  DocumentGroupKey,
  PaymentDocument,
  PaymentDocumentType,
  SINGLE_DOCUMENT_GROUP_ID
} from "../../../types/payments";

/** Required regardless of the legal form of the billing entity. */
export const ALWAYS_REQUIRED_DOCUMENTS: PaymentDocumentType[] = [
  "CITIZEN_CARD_FRONT",
  "CITIZEN_CARD_BACK",
  "PROFESSIONAL_CARD_FRONT",
  "PROFESSIONAL_CARD_BACK",
  "ADDRESS_VALIDATION",
  "INTERNATIONAL_BANK_ACCOUNT_NUMBER"
];

/** Documents each beneficiary / legal representative has to provide. */
export const GROUP_DOCUMENT_TYPES: PaymentDocumentType[] = [
  "CITIZEN_CARD_FRONT",
  "CITIZEN_CARD_BACK",
  "ADDRESS_VALIDATION"
];

/** Single documents required on top of the always-required ones. */
export function requiredDocumentsFor(
  companyType: CompanyType
): PaymentDocumentType[] {
  if (companyType === "INDIVIDUAL_NAME_BUSINESS_MAN") {
    return [...ALWAYS_REQUIRED_DOCUMENTS, "COMPANY_VALIDATION"];
  }
  return [
    ...ALWAYS_REQUIRED_DOCUMENTS,
    "PERMANENT_REGISTRY_CERTIFICATE",
    "OWNERSHIP_AND_BENEFICIARIES"
  ];
}

/**
 * Repeatable groups required for the company type. A sole trader has none —
 * only the incorporated forms need beneficiaries and legal representatives.
 */
export function requiredGroupsFor(
  companyType: CompanyType
): DocumentGroupKey[] {
  if (companyType === "INDIVIDUAL_NAME_BUSINESS_MAN") return [];
  return [
    DOCUMENT_GROUP_KEYS.BENEFICIARIES,
    DOCUMENT_GROUP_KEYS.LEGAL_REPRESENTATION
  ];
}

export const buildIdGroup = (groupKey: DocumentGroupKey, index: number) =>
  `${groupKey}_${index}`;

/**
 * Reads the entry index back out of an idGroup.
 *
 * Both group keys start with a distinct prefix, so a plain startsWith is
 * enough; the index is whatever follows the separating underscore.
 */
export function parseGroupIndex(
  idGroup: string,
  groupKey: DocumentGroupKey
): number | null {
  if (!idGroup.startsWith(`${groupKey}_`)) return null;
  const index = Number.parseInt(idGroup.slice(groupKey.length + 1), 10);
  return Number.isNaN(index) ? null : index;
}

/**
 * Whether a document record corresponds to a file that is actually there.
 *
 * Requesting an upload URL already registers the document on the configuration,
 * so a record exists from the moment the upload starts — including when it then
 * fails. Only the confirmation step sets `stored`, which is what separates a
 * real document from a reservation. The flag is treated as optional: a record
 * that does not carry it at all is taken at face value, so an API that stops
 * returning it does not empty every slot at once.
 */
export function isDocumentStored(document: PaymentDocument): boolean {
  return document.stored !== false;
}

/** Finds an already uploaded single document. */
export function findSingleDocument(
  documents: PaymentDocument[] | undefined,
  documentType: PaymentDocumentType
): PaymentDocument | undefined {
  return documents?.find(
    document =>
      document.documentType === documentType &&
      document.idGroup === SINGLE_DOCUMENT_GROUP_ID &&
      isDocumentStored(document)
  );
}

export interface DocumentGroupEntry {
  /** The index used in the idGroup — stable for the lifetime of the entry. */
  index: number;
  documents: Partial<Record<PaymentDocumentType, PaymentDocument>>;
}

/**
 * Rebuilds the entries of a repeatable group from the documents already stored
 * on the configuration, so reopening the screen shows what was uploaded before.
 */
export function rehydrateGroup(
  documents: PaymentDocument[] | undefined,
  groupKey: DocumentGroupKey
): DocumentGroupEntry[] {
  const byIndex = new Map<number, DocumentGroupEntry>();

  documents?.forEach(document => {
    if (!isDocumentStored(document)) return;
    const index = parseGroupIndex(document.idGroup, groupKey);
    if (index === null) return;

    const entry = byIndex.get(index) ?? { index, documents: {} };
    entry.documents[document.documentType] = document;
    byIndex.set(index, entry);
  });

  return Array.from(byIndex.values()).sort((a, b) => a.index - b.index);
}

/** True when every document of every entry of the group is uploaded. */
export function isGroupComplete(entries: DocumentGroupEntry[]): boolean {
  // At least one entry is required, and each must be fully documented.
  return (
    entries.length >= 1 &&
    entries.every(entry =>
      GROUP_DOCUMENT_TYPES.every(documentType => entry.documents[documentType])
    )
  );
}
